import { Filter } from '@/api/models';
import { EntityList } from '@/components/EntityList';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { queryKeys } from '@/lib/queryKeys';
import { mdiFilter, mdiFilterMultiple } from '@mdi/js';
import * as React from 'react';
import { FiltersFilters } from './FiltersFilters';
import { FiltersTable } from './FiltersTable';

export interface FiltersListProps {
  onRowClick: (filter: Filter) => void;
  onEditFilter: (filter: Filter) => void;
  onDeleteFilter: (filter: Filter) => void;
  onCreateFilter: () => void;
  isDeleting: boolean;
}

export const FiltersList: React.FC<FiltersListProps> = ({
  onRowClick,
  onEditFilter,
  onDeleteFilter,
  onCreateFilter,
  isDeleting,
}) => {
  const listState = usePaginatedList<
    Filter,
    { enabled: string; isMandatory: string }
  >({
    queryKey: queryKeys.ADMIN_FILTERS,
    endpoint: '/admin/filters',
    initialFilters: { enabled: '', isMandatory: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      enabled: filters.enabled ? filters.enabled === 'true' : undefined,
      isMandatory: filters.isMandatory
        ? filters.isMandatory === 'true'
        : undefined,
      sortBy: sortBy as 'name' | 'createdAt',
      sortOrder,
    }),
  });

  return (
    <EntityList
      title='Filters'
      description='Manage business filters for detailed classification'
      icon={mdiFilter}
      countIcon={mdiFilterMultiple}
      countLabel={`${listState.list.total} total filters`}
      createLabel='Add Filter'
      listState={listState}
      onCreateClick={onCreateFilter}
      renderFilters={() => (
        <FiltersFilters
          searchTerm={listState.state.search}
          selectedEnabled={listState.state.filters.enabled}
          selectedIsMandatory={listState.state.filters.isMandatory}
          onSearchChange={listState.actions.setSearch}
          onEnabledChange={value =>
            listState.actions.setFilter('enabled', value)
          }
          onIsMandatoryChange={value =>
            listState.actions.setFilter('isMandatory', value)
          }
          onClearFilters={listState.actions.clearFilters}
        />
      )}
      renderTable={() => (
        <FiltersTable
          filters={listState.list.items}
          isLoading={listState.isLoading}
          sortBy={listState.state.sortBy}
          sortOrder={listState.state.sortOrder}
          onSort={listState.actions.setSort}
          onRowClick={onRowClick}
          onEditFilter={onEditFilter}
          onDeleteFilter={onDeleteFilter}
          isDeleting={isDeleting}
        />
      )}
    />
  );
};
